// src/pages/LoginPage.jsx
import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
  signInWithEmailAndPassword,
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
  sendPasswordResetEmail,
  signOut,
} from "firebase/auth";
import { doc, getDoc } from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";
import { auth, db, googleProvider } from "../firebase";
import { useTranslation } from "react-i18next";
import { SCHOOL } from "../config";

const functions = getFunctions();
// Returns true if the string looks like a mobile number (8–13 digits, optional + prefix)
function looksLikePhone(s) { return /^\+?\d[\d\s-]{7,14}$/.test(s.trim()); }

function friendlyError(code, t) {
  const m = {
    "auth/user-not-found":    t("login.err.userNotFound"),
    "auth/wrong-password":    t("login.err.wrongPassword"),
    "auth/invalid-credential":t("login.err.invalidCred"),
    "auth/invalid-email":     t("login.err.invalidEmail"),
    "auth/too-many-requests": t("login.err.tooMany"),
  };
  return m[code] || t("login.err.default");
}

function isPWA() {
  return window.matchMedia('(display-mode: standalone)').matches ||
         window.navigator.standalone === true;
}

export default function LoginPage() {
  const navigate = useNavigate();
  const { t }    = useTranslation();
  const [tab,      setTab]      = useState("login");

  const [email,    setEmail]    = useState("");
  const [password, setPassword] = useState("");
  const [loading,  setLoading]  = useState(false);
  const [error,    setError]    = useState("");
  const [success,  setSuccess]  = useState("");
  const [showPwd,  setShowPwd]  = useState(false);

  // Handle Google redirect result on page load (PWA mode)
  useEffect(() => {
    getRedirectResult(auth)
      .then(async result => {
        if (!result?.user) return;
        const snap = await getDoc(doc(db, "students", result.user.uid));
        if (!snap.exists()) {
          await signOut(auth);
          setError("No student account found for this Google account. Please register first.");
          return;
        }
        navigate("/");
      })
      .catch(() => {});
  }, [navigate]);

  async function handleLogin(e) {
    e.preventDefault();
    setLoading(true); setError("");
    try {
      let loginEmail = email.trim();
      // If user entered a phone number, resolve it to an email first
      if (looksLikePhone(loginEmail)) {
        try {
          const res = await httpsCallable(functions, 'getEmailByPhone')({ phone: loginEmail });
          loginEmail = res.data.email;
        } catch(phoneErr) {
          setError(phoneErr.message || "No account found with this mobile number.");
          setLoading(false); return;
        }
      }
      await signInWithEmailAndPassword(auth, loginEmail, password);
      navigate("/");
    } catch(err) { setError(friendlyError(err.code, t)); }
    setLoading(false);
  }

  async function handleGoogle() {
    setLoading(true); setError("");
    try {
      if (isPWA()) {
        await signInWithRedirect(auth, googleProvider);
        return; // page redirects; redirect result handled in useEffect above
      }

      let result;
      try {
        result = await signInWithPopup(auth, googleProvider);
      } catch (popupErr) {
        if (popupErr.code === "auth/popup-closed-by-user" ||
            popupErr.code === "auth/cancelled-popup-request") {
          setLoading(false);
          return;
        }
        // Incognito mode, popup blocked, or third-party cookie restrictions
        // — fall back to redirect (same flow as PWA, works in all browsers)
        await signInWithRedirect(auth, googleProvider);
        return; // page redirects; result handled in useEffect above
      }

      const snap = await getDoc(doc(db, "students", result.user.uid));
      if (!snap.exists()) {
        await signOut(auth);
        setError("No student account found for this Google account. Please register first.");
        setLoading(false);
        return;
      }
      navigate("/");
    } catch (err) {
      setError(t("login.err.googleFailed"));
      setLoading(false);
    }
  }

  async function handleForgot(e) {
    e.preventDefault();
    setLoading(true); setError(""); setSuccess("");
    try {
      await sendPasswordResetEmail(auth, email.trim());
      setSuccess(t("login.resetSent"));
    } catch { setError(t("login.resetFailed")); }
    setLoading(false);
  }

  return (
    <div style={{
      position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
      overflowY: "scroll", WebkitOverflowScrolling: "touch",
      background: "white",
    }}>
      <style>{`
        .login-root {
          display: grid;
          grid-template-columns: 1fr 1fr;
          min-height: 100vh;
        }
        .login-brand {
          position: sticky;
          top: 0;
          height: 100vh;
          align-self: start;
          overflow-y: auto;
        }
        .login-form-panel {
          /* no overflow-y — document scrolls freely on mobile */
        }
        @media (max-width: 768px) {
          .login-root {
            grid-template-columns: 1fr;
          }
          .login-brand {
            position: static;
            height: auto;
            overflow-y: visible;
            padding: 28px 24px 24px !important;
            flex-direction: row !important;
            align-items: center !important;
            gap: 14px !important;
            justify-content: flex-start !important;
          }
          .login-brand-body { display: none !important; }
          .login-brand-mobile-row { display: flex !important; }
          .login-form-panel {
            padding: 28px 24px 48px !important;
            justify-content: flex-start !important;
            align-items: flex-start !important;
          }
          .login-form-inner { max-width: 100% !important; }
        }
        @media (min-width: 769px) {
          .login-brand-mobile-row { display: none !important; }
          .login-brand-body { display: block !important; }
        }
      `}</style>

      <div className="login-root">

        {/* LEFT / TOP — Branding */}
        <div className="login-brand" style={{
          background: "linear-gradient(160deg,#4E0808,#7B1515,#9B2020)",
          display: "flex", flexDirection: "column",
          alignItems: "center", justifyContent: "center",
          padding: "60px 48px", color: "white",
        }}>
          <div style={{ maxWidth:"380px", width:"100%" }}>
            {/* Mobile compact row */}
            <div className="login-brand-mobile-row" style={{ alignItems:"center", gap:"12px" }}>
              <picture>
                <source srcSet="/logo.webp" type="image/webp" />
                <img src="/logo.png" alt={SCHOOL.name}
                  style={{ width:"44px", height:"44px", objectFit:"contain", borderRadius:"8px" }}/>
              </picture>
              <div>
                <div style={{ fontWeight:"800", fontSize:"1.05rem", lineHeight:1.2 }}>
                  {SCHOOL.name}
                </div>
                <div style={{ fontSize:".72rem", opacity:.8, marginTop:"2px" }}>
                  {SCHOOL.tagline}
                </div>
              </div>
            </div>

            {/* Desktop full body */}
            <div className="login-brand-body">
              <picture>
                <source srcSet="/logo.webp" type="image/webp" />
                <img src="/logo.png" alt={SCHOOL.name}
                  style={{ width:"140px", height:"140px", objectFit:"contain",
                           borderRadius:"16px", marginBottom:"20px",
                           background:"rgba(255,255,255,.1)", padding:"8px" }}/>
              </picture>
              <h1 style={{ fontSize:"2rem", fontWeight:"900",
                           lineHeight:1.2, marginBottom:"8px" }}>
                {SCHOOL.name}
              </h1>
              <p style={{ fontSize:".9rem", opacity:.75,
                          fontStyle:"italic", marginBottom:"36px", letterSpacing:".3px" }}>
                {SCHOOL.tagline}
              </p>
              <div style={{ borderTop:"1px solid rgba(255,255,255,.2)",
                            paddingTop:"24px",
                            fontSize:".85rem", opacity:.7, lineHeight:2.1 }}>
                <div>👩‍🏫 {SCHOOL.teacher}</div>
                <div>📞 {SCHOOL.phone}</div>
                <div>📧 {SCHOOL.email}</div>
                <div>📍 {SCHOOL.address}</div>
              </div>
            </div>
          </div>
        </div>

        {/* RIGHT / BOTTOM — Form */}
        <div className="login-form-panel" style={{
          display: "flex", alignItems: "center", justifyContent: "center",
          background: "white", padding: "48px",
        }}>
          <div className="login-form-inner" style={{ width:"100%", maxWidth:"400px" }}>

            {tab === "login" ? (
              <>
                <h2 style={{ fontSize:"1.55rem", fontWeight:"800", marginBottom:"4px" }}>
                  {t("login.welcomeBack")}
                </h2>
                <p style={{ color:"#6B7280", marginBottom:"24px", fontSize:".9rem" }}>
                  {t("login.subtitle")}
                </p>

                {error && (
                  <div className="error-box" style={{ marginBottom:"16px" }}>
                    {error}
                  </div>
                )}

                <form onSubmit={handleLogin}>
                  <div className="form-group">
                    <label className="form-label">Email or Mobile Number</label>
                    <input className="form-input" type="text"
                      placeholder="email@example.com or 10-digit mobile"
                      value={email} onChange={e => setEmail(e.target.value)}
                      autoComplete="username"
                      style={{ fontSize:"1rem", padding:"13px 14px" }}
                      required/>
                  </div>
                  <div className="form-group">
                    <label className="form-label">{t("login.passwordLabel")}</label>
                    <div style={{ position:"relative" }}>
                      <input className="form-input"
                        type={showPwd ? "text" : "password"}
                        placeholder="••••••••"
                        value={password}
                        onChange={e => setPassword(e.target.value)}
                        autoComplete="current-password"
                        style={{ paddingRight:"48px", fontSize:"1rem", padding:"13px 48px 13px 14px" }}
                        required/>
                      <button type="button" onClick={() => setShowPwd(!showPwd)}
                        style={{
                          position:"absolute", right:"12px", top:"50%",
                          transform:"translateY(-50%)", background:"none",
                          border:"none", cursor:"pointer", fontSize:"1.1rem",
                          color:"#6B7280", lineHeight:1, padding:"4px",
                        }}>
                        {showPwd ? "🙈" : "👁️"}
                      </button>
                    </div>
                  </div>

                  <div style={{ textAlign:"right", marginBottom:"20px" }}>
                    <button type="button"
                      onClick={() => { setTab("forgot"); setError(""); setSuccess(""); }}
                      style={{
                        background:"none", border:"none", color:"var(--purple-text)",
                        fontSize:".88rem", fontWeight:"600", cursor:"pointer",
                        padding:"4px 0",
                      }}>
                      {t("login.forgotPassword")}
                    </button>
                  </div>

                  <button className="btn btn-primary" type="submit" disabled={loading}
                    style={{ width:"100%", padding:"14px", fontSize:"1rem", borderRadius:"10px" }}>
                    {loading ? <span className="spinner"/> : t("login.signIn")}
                  </button>
                </form>

                <div style={{ display:"flex", alignItems:"center",
                              gap:"12px", margin:"20px 0" }}>
                  <div style={{ flex:1, height:"1px", background:"#E5E7EB" }}/>
                  <span style={{ color:"#9CA3AF", fontSize:".82rem" }}>OR</span>
                  <div style={{ flex:1, height:"1px", background:"#E5E7EB" }}/>
                </div>

                <button className="btn btn-google" onClick={handleGoogle}
                  disabled={loading}
                  style={{
                    width:"100%", padding:"13px", fontSize:".95rem",
                    borderRadius:"10px", boxShadow:"0 1px 4px rgba(0,0,0,.1)",
                  }}>
                  <span style={{ fontWeight:"800", color:"#4285F4", fontSize:"1.1rem" }}>G</span>
                  {t("login.googleBtn")}
                </button>

                <p style={{ textAlign:"center", marginTop:"24px",
                            fontSize:".9rem", color:"#6B7280" }}>
                  {t("login.newStudent")}{" "}
                  <Link to="/register"
                    style={{ color:"var(--purple-text)", fontWeight:"700", textDecoration:"none" }}>
                    {t("login.applyLink")}
                  </Link>
                </p>
              </>

            ) : (
              <>
                <button onClick={() => { setTab("login"); setError(""); setSuccess(""); }}
                  style={{
                    background:"none", border:"none", cursor:"pointer",
                    color:"var(--purple-text)", fontWeight:"600", fontSize:".9rem",
                    marginBottom:"20px", display:"flex", alignItems:"center",
                    gap:"6px", padding:"4px 0",
                  }}>
                  {t("login.backToLogin")}
                </button>
                <h2 style={{ fontSize:"1.55rem", fontWeight:"800", marginBottom:"4px" }}>
                  {t("login.resetTitle")}
                </h2>
                <p style={{ color:"#6B7280", marginBottom:"24px", fontSize:".9rem" }}>
                  {t("login.resetSubtitle")}
                </p>
                {error   && <div className="error-box" style={{ marginBottom:"16px" }}>{error}</div>}
                {success && <div className="success-box" style={{ marginBottom:"16px" }}>{success}</div>}
                <form onSubmit={handleForgot}>
                  <div className="form-group">
                    <label className="form-label">{t("login.emailLabel")}</label>
                    <input className="form-input" type="email"
                      placeholder="your@email.com"
                      value={email} onChange={e => setEmail(e.target.value)}
                      style={{ fontSize:"1rem", padding:"13px 14px" }}
                      required/>
                  </div>
                  <button className="btn btn-primary" type="submit"
                    disabled={loading}
                    style={{ width:"100%", padding:"14px", fontSize:"1rem", borderRadius:"10px" }}>
                    {loading ? <span className="spinner"/> : t("login.sendReset")}
                  </button>
                </form>
              </>
            )}
          </div>
        </div>

      </div>
    </div>
  );
}
